// Stock — per-user record of what you currently have on hand (ingredient stock
// or recipe-stock leftovers).
//
// Stock entries are PRIVATE (PK = the caller's Cognito Identity ID) under
// SK `stock#<uuid>`. An entry holds some servings of EITHER:
//   • a shared ingredient — `{ ingredientRef, servings, obtainedDate, ... }`, or
//   • a stored recipe ("recipe-stock", e.g. leftovers from a multi-serve make) —
//     `{ recipeRef, servings, obtainedDate, ... }`.
// Exactly one of ingredientRef / recipeRef is set. Recipe-stock lets leftover
// prepared servings be carried forward and consumed (no-prep) on later days.
//
//   { ingredientRef | recipeRef, servings, obtainedDate (YYYY-MM-DD),
//     shelfLifeDays? (override; null = indefinite, undefined = use default) }
//
// Effective shelf life: the per-entry override if present, else (for ingredient
// stock) the ingredient record's default. Recipe-stock has no catalogue default,
// so it relies on its own shelfLifeDays (set when the leftover is written).
// Expiry = obtainedDate + effectiveShelfLifeDays. Entries are surfaced
// soonest-expiry-first so the planner uses perishable stock first.

import { addItem, listItems, updateItem, deleteItem } from './db.js';

/** SK prefix for stock entries in a user's private partition. */
export const STOCK_PREFIX = 'stock#';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** True if an entry holds recipe-stock (leftover prepared servings). */
export function isRecipeStock(entry) {
  return Boolean(entry?.recipeRef);
}

/** The ref an entry holds, regardless of kind (recipeRef or ingredientRef). */
export function entryRef(entry) {
  return entry?.recipeRef ?? entry?.ingredientRef ?? null;
}

/**
 * The shelf life that actually applies to an entry: its own override when set
 * (including an explicit null meaning "indefinite"), otherwise the ingredient
 * record's default. Returns null for indefinite, or a number of days.
 */
export function effectiveShelfLifeDays(entry, ingredient) {
  if (entry && Object.prototype.hasOwnProperty.call(entry, 'shelfLifeDays')) {
    return entry.shelfLifeDays; // may be null (indefinite) or a number
  }
  return ingredient?.shelfLifeDays ?? null;
}

/**
 * Expiry date (a Date) for an entry, or null if indefinite or undateable.
 */
export function expiryDate(entry, ingredient) {
  const days = effectiveShelfLifeDays(entry, ingredient);
  if (days === null || days === undefined || !entry?.obtainedDate) return null;
  const obtained = new Date(`${entry.obtainedDate}T00:00:00`);
  if (Number.isNaN(obtained.getTime())) return null;
  return new Date(obtained.getTime() + Number(days) * MS_PER_DAY);
}

/**
 * Format a Date as a local-time YYYY-MM-DD string.
 *
 * NOTE: do NOT use Date.toISOString() for this. expiryDate() builds Dates at
 * LOCAL midnight (e.g. Sydney 00:00). toISOString() converts to UTC, which for
 * any timezone ahead of UTC rolls back to the previous calendar day — so an
 * expiry of 20/6 would display as 19/6. Reading the local Y/M/D components
 * avoids that shift.
 */
export function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole days from today (local midnight) until the entry expires. Negative if
 * already expired, 0 if it expires today, null if indefinite/undateable.
 */
export function daysUntilExpiry(entry, ingredient, now = new Date()) {
  const expiry = expiryDate(entry, ingredient);
  if (!expiry) return null;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((expiry - startOfToday) / MS_PER_DAY);
}

/**
 * Coarse expiry status for UI colour cues:
 *   'expired'    — expiry date has passed
 *   'soon'       — expires within `soonDays` (default 2) days
 *   'ok'         — expires later
 *   'indefinite' — no expiry
 */
export function expiryStatus(entry, ingredient, soonDays = 2, now = new Date()) {
  const days = daysUntilExpiry(entry, ingredient, now);
  if (days === null) return 'indefinite';
  if (days < 0) return 'expired';
  if (days <= soonDays) return 'soon';
  return 'ok';
}

/**
 * Compare two (possibly null) expiry Dates; soonest first, null (indefinite =
 * effectively infinite) last. A small comparator shared by the planner and the
 * editor so both consume perishable stock first.
 */
export function byExpiry(a, b) {
  if (a && b) return a - b;
  if (a) return -1; // a expires, b is indefinite -> a first
  if (b) return 1;
  return 0;
}

/**
 * Sort entries soonest-expiry-first. Indefinite (null expiry) sort last.
 * `ingredientFor(entry)` resolves the entry's ingredient record (for its
 * default shelf life). Returns a new sorted array.
 */
export function sortByExpiry(entries, ingredientFor) {
  return [...entries].sort((a, b) =>
    byExpiry(expiryDate(a, ingredientFor(a)), expiryDate(b, ingredientFor(b))),
  );
}

/**
 * Allocate reserved SERVINGS (a per-key quantity, e.g. reserved-per-ingredient
 * from `reservationsFromMenus`) across the individual stock ROWS of that key,
 * soonest-expiry-first — so the rows a menu will actually draw (the perishable
 * ones) are the ones marked reserved. PURE.
 *
 * Keys a row by ingredientRef OR recipeRef (whichever it holds), so ingredient
 * stock and recipe-stock leftovers are handled by the same pass. Rows whose key
 * has no reservation get 0.
 *
 * @param entries        raw stock rows (as listStock returns)
 * @param reservedByKey  Map<ref, servings> reserved per ingredient/recipe ref
 * @param ingredientFor  (entry) => ingredient record | null (for expiry sort)
 * @returns Map<itemId, reservedServings>  — reserved portion of each row
 *          (0 ≤ reserved ≤ row.servings; a row may be partially reserved)
 */
export function allocateReserved(entries, reservedByKey, ingredientFor) {
  const reservedByRow = new Map();
  if (!reservedByKey || reservedByKey.size === 0) return reservedByRow;

  // Group rows by their ref, then walk each group soonest-expiry-first, filling
  // the key's reserved quantity row by row.
  const byKey = new Map();
  for (const entry of entries ?? []) {
    const key = entryRef(entry);
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(entry);
  }

  for (const [key, remainingWanted] of reservedByKey) {
    const rows = sortByExpiry(byKey.get(key) ?? [], ingredientFor);
    let left = Math.max(0, Number(remainingWanted) || 0);
    for (const row of rows) {
      if (left <= 0) break;
      const have = Math.max(0, Number(row.servings) || 0);
      const take = Math.min(have, left);
      if (take > 0) {
        reservedByRow.set(row.itemId, take);
        left -= take;
      }
    }
  }
  return reservedByRow;
}

/** List all stock entries for the signed-in user (their private rows). */
export function listStock(doc, userId) {
  return listItems(doc, userId, STOCK_PREFIX);
}

/**
 * Add a stock entry. `entry` must include ingredientRef|recipeRef + servings +
 * obtainedDate, and may include a shelfLifeDays override. Returns the stored row
 * (with generated itemId).
 *
 * Pass `stockRef` to set the row's itemId explicitly (e.g. so a generator-minted
 * ref that menu entries already point at becomes the persisted id). The prefix
 * is added if absent so the row is always queryable under STOCK_PREFIX.
 */
export function addStockEntry(doc, userId, entry, stockRef = null) {
  const itemId = stockRef
    ? stockRef.startsWith(STOCK_PREFIX)
      ? stockRef
      : STOCK_PREFIX + stockRef
    : null;
  return addItem(doc, userId, STOCK_PREFIX, entry, itemId);
}

/** Partially update a stock entry (e.g. change servings). */
export function updateStockEntry(doc, userId, itemId, attrs) {
  return updateItem(doc, userId, itemId, attrs);
}

/** Delete a stock entry by its itemId. */
export function deleteStockEntry(doc, userId, itemId) {
  return deleteItem(doc, userId, itemId);
}
